import { authDelete, authPost } from './authHttpClient'
import { getAccessManagementAuthUrl } from './rulesEngineEndpoints'

// Configuravel por ambiente -- default mantem o comportamento ja validado (2 min).
const TOKEN_REFRESH_MARGIN_MS = Number(process.env.REACT_APP_TOKEN_REFRESH_MARGIN_MS) || 120000
const TOKEN_STORAGE_KEY = 'aml_engine_access_token'
const TOKEN_EXPIRES_AT_STORAGE_KEY = 'aml_engine_access_token_expires_at_ms'

let cachedToken = null
let tokenExpiresAtMs = 0
let cachedClaims = null
let authenticationPromise = null

const getTrimmedEnv = (value) => (typeof value === 'string' ? value.trim() : '')

const hasStorage = () => typeof localStorage !== 'undefined'

const decodeTokenClaims = (token) => {
  if (!token || typeof token !== 'string') {
    return null
  }

  const parts = token.split('.')

  if (parts.length < 2) {
    return null
  }

  try {
    const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    const normalizedPayload = payload.padEnd(payload.length + ((4 - (payload.length % 4)) % 4), '=')
    const decoded = atob(normalizedPayload)

    return JSON.parse(decoded)
  } catch {
    return null
  }
}

const setStorageSession = (token, expiresAtMs) => {
  if (!hasStorage()) {
    return
  }

  localStorage.setItem(TOKEN_STORAGE_KEY, token)
  localStorage.setItem(TOKEN_EXPIRES_AT_STORAGE_KEY, String(expiresAtMs))
}

const clearStorageSession = () => {
  if (!hasStorage()) {
    return
  }

  localStorage.removeItem(TOKEN_STORAGE_KEY)
  localStorage.removeItem(TOKEN_EXPIRES_AT_STORAGE_KEY)
}

const loadSessionFromStorage = () => {
  if (cachedToken || !hasStorage()) {
    return
  }

  const token = getTrimmedEnv(localStorage.getItem(TOKEN_STORAGE_KEY))
  const expiresAtRaw = Number(localStorage.getItem(TOKEN_EXPIRES_AT_STORAGE_KEY))

  if (!token || !Number.isFinite(expiresAtRaw) || expiresAtRaw <= 0) {
    clearStorageSession()
    return
  }

  cachedToken = token
  tokenExpiresAtMs = expiresAtRaw
  cachedClaims = decodeTokenClaims(token)
}

const resolveExpirationMs = (data) => {
  if (Number.isFinite(data?.expires_in)) {
    return Date.now() + Number(data.expires_in) * 1000
  }

  if (Number.isFinite(data?.expires_at)) {
    return Number(data.expires_at)
  }

  if (typeof data?.expires_at === 'string') {
    const parsed = Date.parse(data.expires_at)

    if (!Number.isNaN(parsed)) {
      return parsed
    }
  }

  throw new Error('Rules engine auth response missing expiration fields')
}

const resolveToken = (data) => {
  const token = getTrimmedEnv(data?.access_token || data?.token)

  if (!token) {
    throw new Error('Rules engine auth response missing token')
  }

  return token
}

const isTokenStillValid = () => cachedToken && Date.now() + TOKEN_REFRESH_MARGIN_MS < tokenExpiresAtMs

const setSession = (token, expiresAtMs) => {
  cachedToken = token
  tokenExpiresAtMs = expiresAtMs
  cachedClaims = decodeTokenClaims(token)
  setStorageSession(token, expiresAtMs)
}

export const clearRulesEngineSession = () => {
  cachedToken = null
  tokenExpiresAtMs = 0
  cachedClaims = null
  authenticationPromise = null
  clearStorageSession()
}

const resolveCredentialInput = (value, fieldName) => {
  const normalized = getTrimmedEnv(value)

  if (!normalized) {
    throw new Error(`${fieldName} is required`)
  }

  return normalized
}

export const authenticate = async ({ email, password } = {}) => {
  if (authenticationPromise) {
    return authenticationPromise
  }

  const requestUrl = getAccessManagementAuthUrl()
  const resolvedEmail = resolveCredentialInput(email, 'email')
  const resolvedPassword = resolveCredentialInput(password, 'password')

  authenticationPromise = authPost(requestUrl, {
    email: resolvedEmail,
    password: resolvedPassword,
  }).then((response) => {
    const token = resolveToken(response?.data)
    const expiresAtMs = resolveExpirationMs(response?.data)

    setSession(token, expiresAtMs)

    return {
      token,
      expiresAtMs,
      role: cachedClaims?.roles?.[0] || cachedClaims?.role || null,
      roles: cachedClaims?.roles || [],
      clientId: cachedClaims?.client_id || cachedClaims?.sub || null,
      claims: cachedClaims,
    }
  }).finally(() => {
    authenticationPromise = null
  })

  return authenticationPromise
}

export const revokeTechnicalSession = async () => {
  loadSessionFromStorage()

  const token = cachedToken
  const requestUrl = getAccessManagementAuthUrl()

  clearRulesEngineSession()

  if (!token) {
    return false
  }

  try {
    await authDelete(requestUrl, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
    return true
  } catch {
    return false
  }
}

export const getValidToken = async () => {
  loadSessionFromStorage()

  if (isTokenStillValid()) {
    return cachedToken
  }

  clearRulesEngineSession()
  const error = new Error('Authentication required')
  error.isSessionExpired = true
  throw error
}

export const getRulesEngineSessionSnapshot = () => ({
  token: cachedToken,
  expiresAtMs: tokenExpiresAtMs,
  role: cachedClaims?.role || null,
  clientId: cachedClaims?.client || null,
  claims: cachedClaims,
  isNearExpiry: !cachedToken || Date.now() + TOKEN_REFRESH_MARGIN_MS >= tokenExpiresAtMs,
})
